import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, Heart } from "lucide-react";
import PhoneMockup from "./PhoneMockup";
import AppStoreButton from "./AppStoreButton";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

export default function Hero() {
    const reduce = useReducedMotion();
    const anim = reduce ? {} : REVEAL;

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden"
        >
            {/* Decorative washes */}
            <div className="absolute inset-0 hero-wash pointer-events-none" />
            <div className="noise-overlay" />

            <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-20 pb-20 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                    <div className="lg:col-span-6">
                        <motion.div
                            {...anim}
                            className="inline-flex items-center gap-2 rounded-full border border-brand bg-white/70 backdrop-blur px-3 py-1.5"
                            data-testid="hero-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-teal-2)] animate-pulse" />
                            <span className="text-[11.5px] font-semibold tracking-[0.12em] uppercase text-[var(--brand-muted-2)]">
                                For roommates, couples & families
                            </span>
                        </motion.div>

                        <motion.h1
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.05,
                            }}
                            data-testid="hero-headline"
                            className="mt-5 font-display text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.02] tracking-[-0.025em] text-[var(--brand-ink)]"
                        >
                            A calmer way
                            <br />
                            through{" "}
                            <span className="italic text-[var(--brand-navy)]">
                                conflict.
                            </span>
                        </motion.h1>

                        <motion.p
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.1,
                            }}
                            className="mt-5 max-w-[56ch] text-[16px] sm:text-[17.5px] leading-relaxed text-[var(--brand-muted-2)]"
                        >
                            BetweenUs helps two people share their side
                            privately, see a neutral map of the issue, and
                            choose a fair next step — together. No judgment,
                            no sides, no shouting match.
                        </motion.p>

                        <motion.div
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.15,
                            }}
                            id="download"
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <AppStoreButton
                                variant="ios"
                                testId="hero-appstore-ios-button"
                            />
                            <AppStoreButton
                                variant="android"
                                testId="hero-appstore-android-button"
                            />
                        </motion.div>

                        <motion.ul
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.2,
                            }}
                            className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-[var(--brand-muted-2)]"
                        >
                            <li
                                className="inline-flex items-center gap-2"
                                data-testid="hero-trust-private"
                            >
                                <Lock
                                    size={14}
                                    className="text-[var(--brand-teal-2)]"
                                />
                                Private by default
                            </li>
                            <li
                                className="inline-flex items-center gap-2"
                                data-testid="hero-trust-safety"
                            >
                                <ShieldCheck
                                    size={14}
                                    className="text-[var(--brand-teal-2)]"
                                />
                                Safety-first design
                            </li>
                            <li
                                className="inline-flex items-center gap-2"
                                data-testid="hero-trust-async"
                            >
                                <Heart
                                    size={14}
                                    className="text-[var(--brand-teal-2)]"
                                />
                                Works async — no live confrontation
                            </li>
                        </motion.ul>

                        <motion.p
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.25,
                            }}
                            className="mt-6 text-[12px] text-[var(--brand-muted)]"
                        >
                            Not therapy. Not legal advice. Not crisis
                            counseling.
                        </motion.p>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <PhoneMockup />
                    </div>
                </div>
            </div>
        </section>
    );
}
